/**
 * Dashboard Service
 * Handles all API calls for dashboard data including stats, activity, and charts
 */

import { api_service } from './api';
import {
  DashboardStats,
  RecentActivity,
  RevenueData,
  ConsumptionData,
  DashboardFilters
} from '@/types/dashboard_types';

/**
 * Fetch dashboard statistics
 * Returns key metrics for meters, invoices, payments, and clients
 */
export const fetch_dashboard_stats = async (): Promise<DashboardStats> => {
  try {
    const response = await api_service.get('/dashboard/stats/');
    return response;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

/**
 * Fetch recent activity feed
 * @param params - Optional parameters for filtering activity
 * @param params.limit - Maximum number of activities to return (default: 20)
 */
export const fetch_recent_activity = async (
  params?: { limit?: number }
): Promise<RecentActivity[]> => {
  try {
    const response = await api_service.get('/dashboard/recent-activity/', {
      params: {
        limit: params?.limit || 20
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching recent activity:', error);
    throw error;
  }
};

/**
 * Fetch revenue data for charts
 * @param filters - Dashboard filters including time period
 */
export const fetch_revenue_data = async (
  filters: DashboardFilters
): Promise<RevenueData[]> => {
  try {
    const response = await api_service.get('/dashboard/revenue/', {
      params: {
        period: filters.period
      }
    });

    console.log(" revenue data", response)
    return response;
  } catch (error) {
    console.error('Error fetching revenue data:', error);
    throw error;
  }
};

/**
 * Fetch consumption data for charts
 * @param filters - Dashboard filters including time period
 */
export const fetch_consumption_data = async (
  filters: DashboardFilters
): Promise<ConsumptionData[]> => {
  try {
    const response = await api_service.get('/dashboard/consumption/', {
      params: {
        period: filters.period
      }
    });
    return response;
  } catch (error) {
    console.error('Error fetching consumption data:', error);
    throw error;
  }
};

/**
 * Fetch dashboard overview (combined stats, activity, and charts)
 * This is an optimized endpoint that returns all dashboard data in one request
 * @param filters - Dashboard filters
 */
export const fetch_dashboard_overview = async (
  filters: DashboardFilters
): Promise<{
  stats: DashboardStats;
  recent_activity: RecentActivity[];
  revenue_data: RevenueData[];
  consumption_data: ConsumptionData[];
}> => {
  try {
    const response = await api_service.get('/dashboard/overview/', {
      params: {
        period: filters.period,
        activity_limit: 20
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard overview:', error);
    throw error;
  }
};

/**
 * Export dashboard data to PDF
 * @param filters - Dashboard filters for the export
 */
export const export_dashboard_pdf = async (
  filters: DashboardFilters
): Promise<Blob> => {
  try {
    const response = await api_service.get('/dashboard/export/pdf/', {
      params: {
        period: filters.period
      },
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting dashboard to PDF:', error);
    throw error;
  }
};

/**
 * Export dashboard data to Excel
 * @param filters - Dashboard filters for the export
 */
export const export_dashboard_excel = async (
  filters: DashboardFilters
): Promise<Blob> => {
  try {
    const response = await api_service.get('/dashboard/export/excel/', {
      params: {
        period: filters.period
      },
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting dashboard to Excel:', error);
    throw error;
  }
};

/**
 * Fetch alerts and notifications for the dashboard
 * Returns issues requiring immediate attention
 */
export const fetch_dashboard_alerts = async (): Promise<{
  faulty_meters: number;
  meters_needing_reading: number;
  overdue_invoices: number;
  low_balance_clients: number;
}> => {
  try {
    const response = await api_service.get('/dashboard/alerts/');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard alerts:', error);
    throw error;
  }
};

/**
 * Fetch comparison data for period-over-period analysis
 * @param filters - Dashboard filters including period
 */
export const fetch_comparison_data = async (
  filters: DashboardFilters
): Promise<{
  current_period: {
    revenue: number;
    consumption: number;
    new_clients: number;
    invoices_generated: number;
  };
  previous_period: {
    revenue: number;
    consumption: number;
    new_clients: number;
    invoices_generated: number;
  };
  percentage_changes: {
    revenue: number;
    consumption: number;
    new_clients: number;
    invoices_generated: number;
  };
}> => {
  try {
    const response = await api_service.get('/dashboard/comparison/', {
      params: {
        period: filters.period
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching comparison data:', error);
    throw error;
  }
};

/**
 * Fetch top clients by consumption or revenue
 * @param params - Parameters for filtering top clients
 */
export const fetch_top_clients = async (params?: {
  metric?: 'consumption' | 'revenue';
  period?: DashboardFilters['period'];
  limit?: number;
}): Promise<Array<{
  client_id: string;
  client_name: string;
  total_consumption?: number;
  total_revenue?: number;
  invoice_count: number;
}>> => {
  try {
    const response = await api_service.get('/dashboard/top-clients/', {
      params: {
        metric: params?.metric || 'revenue',
        period: params?.period || 'month',
        limit: params?.limit || 10
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching top clients:', error);
    throw error;
  }
};

/**
 * Fetch payment method distribution
 * @param filters - Dashboard filters
 */
export const fetch_payment_distribution = async (
  filters: DashboardFilters
): Promise<Array<{
  payment_method: string;
  total_amount: number;
  transaction_count: number;
  percentage: number;
}>> => {
  try {
    const response = await api_service.get('/dashboard/payment-distribution/', {
      params: {
        period: filters.period
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching payment distribution:', error);
    throw error;
  }
};